import { ref } from 'lit/directives/ref.js'

/**
 * Collect refs from elements and bind them to another element
 *
 * @example
 * class MyComponent extends LfuiElement {
 *   #refs = refCollection()
 *
 *   render() {
 *     return html`
 *       <label ${this.#refs.add()}>My label</label>
 *       <input type="text" ${this.#refs.bind('ariaLabelledByElements')} />
 *       <p ${this.#refs.add()}>
 *         My description
 *       </p>
 *     `
 *   }
 * }
 */
export default function refCollection() {
	const bindings = new Map<
		Element,
		string | ((el: Element, refs: Element[]) => void)
	>()

	const refs = new Set<Element>()

	let queued = false
	const update = () => {
		if (queued) return
		queued = true
		window.requestAnimationFrame(() => {
			queued = false
			for (const [element, prop] of bindings.entries()) {
				if (typeof prop === 'function') {
					prop(element, Array.from(refs))
				} else {
					const key = String(prop)
					const incoming = Array.from(refs)
					const existing = Array.isArray((element as any)[key])
						? (element as any)[key].filter(
								(existingRef: Element) => !incoming.includes(existingRef)
							)
						: []
					;(element as any)[key] = incoming.concat(existing)
				}
			}
		})
	}

	return {
		add() {
			return ref((el: Element) => {
				if (el) {
					refs.add(el)
					update()
				}
			})
		},
		bind(prop: string | ((el: Element, refs: Element[]) => void)) {
			return ref((el: Element) => {
				if (el) {
					bindings.set(el, prop)
					update()
				}
			})
		}
	}
}
